import React, { useState, useEffect, useMemo } from "react";
import PatientSearch, { PatientResult } from "../../doctor/PatientSearch";
import InteractiveToothChart from "../../manager/billing/InteractiveToothChart";
import { apiGet } from "../../../services/api";
import { loadProcedureRegistry, getProcedureCost } from "../../../registry/procedureRegistry";
import "./CrownBridgeEstimate.css";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------

interface Rates {
  rct_anterior: number;
  rct_posterior: number;
  abutment_prep: number; // ABUTMENT_PREP
}

interface MaterialPrice {
  material: string;
  unit_cost: number;
}

interface ToothConfig {
  rct: boolean;
  prep: boolean;
}

interface EstimateRow {
  key: string;
  materialLabel: string;
  unitPrice: number;
  materialTotal: number;
  prepTotal: number;
  rctTotal: number;
  grandTotal: number;
}

const DEFAULT_RATES: Rates = {
  rct_anterior: 3500,
  rct_posterior: 5500,
  abutment_prep: 8000,
};

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

/**
 * Returns true if the tooth is Anterior (13-23, 33-43).
 * IOS/FDI Notation: 11,12,13, 21,22,23, 31,32,33, 41,42,43
 */
function isAnterior(tooth: number): boolean {
  const t = Number(tooth);
  return [11, 12, 13, 21, 22, 23, 31, 32, 33, 41, 42, 43].includes(t);
}

// ----------------------------------------------------------------------
// Component: CrownBridgeEstimate
// ----------------------------------------------------------------------

const CrownBridgeEstimate: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  // -- State --
  const [patient, setPatient] = useState<PatientResult | null>(null);

  // Tooth selection state
  const [selectedTeeth, setSelectedTeeth] = useState<string[]>([]);

  // Per-tooth configuration: Map tooth ID -> Config
  const [toothConfigs, setToothConfigs] = useState<Record<string, ToothConfig>>({});

  // Rate & Config State
  const [rates, setRates] = useState<Rates>(DEFAULT_RATES);
  const [crownMaterialPrices, setCrownMaterialPrices] = useState<MaterialPrice[]>([]);

  // -- Load Data --
  useEffect(() => {
    loadProcedureRegistry().then(() => {
      // 1. Fetch Rates from Registry (Admin Master Data)
      // Keys: RCT_ANTERIOR, RCT_POSTERIOR, ABUTMENT_PREP
      const rctAnt = getProcedureCost("RCT_ANTERIOR") || DEFAULT_RATES.rct_anterior;
      const rctPost = getProcedureCost("RCT_POSTERIOR") || DEFAULT_RATES.rct_posterior;
      const crownPrep = getProcedureCost("ABUTMENT_PREP") || DEFAULT_RATES.abutment_prep;

      setRates({
        rct_anterior: rctAnt,
        rct_posterior: rctPost,
        abutment_prep: crownPrep,
      });
    });

    // 2. Fetch Crown Materials (Manager -> Lab -> Patient Price)
    apiGet("/api/manager/crown-material-prices")
      .then((res: unknown) => {
        const list = Array.isArray(res) ? (res as MaterialPrice[]) : [];
        setCrownMaterialPrices(list);
      })
      .catch((err) => {
        console.error("Failed to fetch crown prices", err);
      });
  }, []);

  // -- Effects --

  // Initialize/Update Tooth Config when selection changes
  useEffect(() => {
    setToothConfigs((prev) => {
      const next = { ...prev };
      // Remove unselected
      Object.keys(next).forEach((t) => {
        if (!selectedTeeth.includes(t)) {
          delete next[t];
        }
      });
      // Add new selected (default: Prep=True, RCT=False)
      selectedTeeth.forEach((t) => {
        if (!next[t]) {
          next[t] = { rct: false, prep: true };
        }
      });
      return next;
    });
  }, [selectedTeeth]);

  // -- Computations --

  const totalCount = selectedTeeth.length;

  // Calculate Costs based on per-tooth config
  const { rctCostTotal, prepCostTotal } = useMemo(() => {
    let rct = 0;
    let prep = 0;

    selectedTeeth.forEach((t) => {
      const config = toothConfigs[t];
      if (!config) return;

      if (config.rct) {
        if (isAnterior(Number(t))) {
          rct += rates.rct_anterior;
        } else {
          rct += rates.rct_posterior;
        }
      }

      if (config.prep) {
        prep += rates.abutment_prep;
      }
    });

    return { rctCostTotal: rct, prepCostTotal: prep };
  }, [selectedTeeth, toothConfigs, rates]);


  // Rows (One for each material)
  const rows: EstimateRow[] = useMemo(() => {
    let materials = crownMaterialPrices;
    if (materials.length === 0) {
      materials = [
        { material: "Metal", unit_cost: 3000 },
        { material: "PFM", unit_cost: 5000 },
        { material: "Zirconia", unit_cost: 8000 },
      ];
    }

    return materials.map((m) => {
      const matTotal = totalCount * (m.unit_cost || 0);
      return {
        key: m.material,
        materialLabel: m.material,
        unitPrice: m.unit_cost || 0,
        materialTotal: matTotal,
        prepTotal: prepCostTotal,
        rctTotal: rctCostTotal,
        grandTotal: matTotal + prepCostTotal + rctCostTotal,
      };
    });
  }, [crownMaterialPrices, totalCount, prepCostTotal, rctCostTotal]);

  // -- Handlers --

  const handlePrint = () => {
    window.print();
  };

  const toggleToothConfig = (tooth: string, field: keyof ToothConfig) => {
    setToothConfigs((prev) => ({
      ...prev,
      [tooth]: {
        ...prev[tooth],
        [field]: !prev[tooth][field],
      },
    }));
  };

  // -- Visuals --
  const { chartStates, chartOverlays } = useMemo(() => {
    const states: Record<number, string> = {};
    const rctList: number[] = [];

    selectedTeeth.forEach((t) => {
      const toothNum = Number(t);
      const config = toothConfigs[t];
      if (!config) return;

      if (config.prep) {
        states[toothNum] = "Crown_Cutting"; // or Abutment_Prep
      } else if (config.rct) {
        // If only RCT and no prep, maybe show RCT_Done as state? 
        // But usually RCT is overlay. Let's stick to overlay for RCT.
      }

      if (config.rct) {
        rctList.push(toothNum);
      }
    });

    return {
      chartStates: states,
      chartOverlays: { rct: rctList }
    };
  }, [selectedTeeth, toothConfigs]);

  // -- Render --

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
      
      {/* Left Column: Input & Charting */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', boxShadow: 'var(--mod-shadow-sm)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1.05rem', color: 'var(--mod-text-main)', display: 'flex', justifyContent: 'space-between' }}>
            <span>1. Select Patient</span>
            {onClose && <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--mod-text-muted)' }}>Close</button>}
          </h3>
          <PatientSearch onSelect={setPatient} />
          {patient && (
            <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
              <strong style={{ color: 'var(--mod-text-main)' }}>{patient.name}</strong> <span style={{ color: 'var(--mod-text-muted)' }}>({patient.mobile || "No phone"})</span>
            </div>
          )}
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', boxShadow: 'var(--mod-shadow-sm)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1.05rem', color: 'var(--mod-text-main)' }}>2. Tooth Charting</h3>
          <InteractiveToothChart
            selectedTeeth={selectedTeeth.map(t => parseInt(t, 10)).filter(n => !isNaN(n))}
            onChange={(teeth) => setSelectedTeeth(teeth.map(t => String(t)))}
            states={chartStates}
            overlays={chartOverlays}
          />
          {selectedTeeth.length > 0 && (
            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {selectedTeeth.map((t) => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ color: 'var(--mod-sky)' }}>Tooth {t}</strong>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--mod-text-main)' }}>
                      <input type="checkbox" checked={toothConfigs[t]?.prep || false} onChange={(e) => toggleToothConfig(t, "prep")} />
                      Crown Prep
                    </label>
                    <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--mod-text-main)' }}>
                      <input type="checkbox" checked={toothConfigs[t]?.rct || false} onChange={(e) => toggleToothConfig(t, "rct")} />
                      RCT
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Column: Dynamic Estimates */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', boxShadow: 'var(--mod-shadow-md)', height: 'fit-content' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '1.2rem', color: 'var(--mod-text-main)' }}>3. Cost Estimates</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.9rem', color: 'var(--mod-text-muted)' }}>Comparison based on selected materials.</p>
          </div>
          <button style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#f8fafc', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: 'var(--mod-text-main)' }} onClick={handlePrint}>Print</button>
        </div>
        
        {selectedTeeth.length === 0 ? (
          <div className="mod-empty-state" style={{ height: '200px', border: '1px dashed #cbd5e1', background: 'transparent' }}>
             <span style={{ fontSize: '2rem' }}>🦷</span>
             <p style={{ marginTop: '8px' }}>Select teeth on the chart to generate estimates.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {rows.map((row) => (
              <div key={row.key} style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 800, color: 'var(--mod-text-main)', fontSize: '1.05rem' }}>{row.materialLabel}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--mod-text-muted)' }}>Base: ₹{row.unitPrice.toLocaleString()} / unit</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--mod-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Total Estimate</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--mod-sky)' }}>₹{row.grandTotal.toLocaleString()}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#475569' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span>Crowns/Bridges: <strong style={{ color: 'var(--mod-text-main)' }}>₹{row.materialTotal.toLocaleString()}</strong></span>
                    <span>Abutment Prep: <strong style={{ color: 'var(--mod-text-main)' }}>₹{row.prepTotal.toLocaleString()}</strong></span>
                    <span>RCT Procedures: <strong style={{ color: 'var(--mod-text-main)' }}>₹{row.rctTotal.toLocaleString()}</strong></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CrownBridgeEstimate;
